import classNames from 'classnames'
import React from 'react'

import { optimizeMessagingAttachmentUrl } from '../../../../../utils/cdnImageUrl'
import { AttachmentThumbnail, renderTypeIcon } from '../../../../AttachmentCard'
import type { LockedAttachmentSource, PaymentStatus } from '../../types'

import { LockBadge } from './LockBadge'
import type { CardVariant } from './MediaShell'

export interface SingleThumbnailProps {
  variant: CardVariant
  mimeType: string
  thumbnailUrl?: string
  title?: string
  /**
   * When provided, the underlying source is shown unblurred (preview / unlocked state).
   * When undefined, the thumbnail is treated as locked: blurred with a central lock badge.
   */
  source?: LockedAttachmentSource
  /**
   * Forces the locked appearance even when a source is provided. Used for the
   * Composer card where the locked treatment is the default and the user toggles
   * a preview in place.
   */
  showLocked: boolean
  paymentStatus?: PaymentStatus
  /** Top-right overlay (e.g. dismiss X on Composer). Rendered inside the thumbnail. */
  topRight?: React.ReactNode
  /** When true, renders the unlocked image with aspect-video + object-contain. */
  containedImage?: boolean
}

/**
 * Renders the 180px-tall media area for a LockedAttachment card.
 * Handles three visual states:
 *   - locked:       blurred thumbnail + centered 48px lock badge
 *   - preview/paid: clear thumbnail (the underlying source)
 *   - empty:        type-icon placeholder
 */
const SingleThumbnail: React.FC<SingleThumbnailProps> = ({
  variant,
  mimeType,
  thumbnailUrl,
  title,
  source,
  showLocked,
  paymentStatus,
  topRight,
  containedImage = false,
}) => {
  const effectiveSourceUrl = showLocked ? undefined : source?.sourceUrl
  const effectiveThumbnailUrl = showLocked
    ? thumbnailUrl
    : (source?.thumbnailUrl ?? thumbnailUrl)
  const optimizedThumbnailUrl = optimizeMessagingAttachmentUrl(
    effectiveThumbnailUrl
  )

  const isDark = variant === 'dark'

  // Locked + unlocked render entirely separate trees so the locked state
  // doesn't end up stacking a blurred `<img>` on top of an underlying
  // `AttachmentThumbnail` (whose `bg-white/10` poster shell otherwise
  // shows through as a visible horizontal seam at the 16:9 boundary).
  //
  // The locked branch uses `filter: blur` on a single full-size `<img>`
  // — not `backdrop-filter` — because `backdrop-filter` paints in its
  // own compositing layer that escapes ancestor `overflow: hidden` on
  // Chrome/Safari and flattens the card's top corners. `filter` is
  // clipped normally, so the rounded corners stay round without any
  // clip-path workarounds.
  return (
    <div className="relative overflow-hidden rounded-t-md">
      {showLocked ? (
        <div
          className={classNames(
            'relative aspect-video overflow-hidden',
            isDark ? 'bg-white/10' : 'bg-black/5'
          )}
        >
          {effectiveThumbnailUrl ? (
            <img
              src={optimizedThumbnailUrl ?? effectiveThumbnailUrl}
              alt=""
              aria-hidden
              draggable={false}
              // `scale-110` hides the fuzzy semi-transparent edge that
              // `filter: blur` leaves around the image bounds.
              className="absolute inset-0 h-full w-full scale-110 object-cover blur-[40px]"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              {renderTypeIcon(mimeType, {
                className: isDark
                  ? 'size-12 text-white/20'
                  : 'size-12 text-black/20',
                weight: 'regular',
              })}
            </div>
          )}
          <LockBadge
            paymentStatus={paymentStatus}
            mimeType={mimeType}
            isLocked={showLocked}
          />
        </div>
      ) : (
        <AttachmentThumbnail
          mimeType={mimeType}
          sourceUrl={effectiveSourceUrl}
          thumbnailUrl={effectiveThumbnailUrl}
          title={title}
          variant={variant}
          containedImage={containedImage}
          mediaPlayerProps={
            effectiveSourceUrl
              ? { autoPlay: true, loop: true, controls: true, muted: false }
              : undefined
          }
        />
      )}

      {topRight && (
        <div className="pointer-events-auto absolute right-3 top-3 z-10">
          {topRight}
        </div>
      )}
    </div>
  )
}

export default SingleThumbnail
